import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { BackgroundServiceApi } from '../../util/api';

// ================ Action types ================ //

export const FETCH_BALANCE_REQUEST = 'app/StripePayoutPage/FETCH_BALANCE_REQUEST';
export const FETCH_BALANCE_SUCCESS = 'app/StripePayoutPage/FETCH_BALANCE_SUCCESS';
export const FETCH_BALANCE_ERROR = 'app/StripePayoutPage/FETCH_BALANCE_ERROR';

// Connect account actions
export const CREATE_CONNECT_ACCOUNT_REQUEST = 'app/StripePayoutPage/CREATE_CONNECT_ACCOUNT_REQUEST';
export const CREATE_CONNECT_ACCOUNT_SUCCESS = 'app/StripePayoutPage/CREATE_CONNECT_ACCOUNT_SUCCESS';
export const CREATE_CONNECT_ACCOUNT_ERROR = 'app/StripePayoutPage/CREATE_CONNECT_ACCOUNT_ERROR';

export const FETCH_CONNECT_STATUS_REQUEST = 'app/StripePayoutPage/FETCH_CONNECT_STATUS_REQUEST';
export const FETCH_CONNECT_STATUS_SUCCESS = 'app/StripePayoutPage/FETCH_CONNECT_STATUS_SUCCESS';
export const FETCH_CONNECT_STATUS_ERROR = 'app/StripePayoutPage/FETCH_CONNECT_STATUS_ERROR';

export const CONNECT_LINK_REQUEST = 'app/StripePayoutPage/CONNECT_LINK_REQUEST';
export const CONNECT_LINK_SUCCESS = 'app/StripePayoutPage/CONNECT_LINK_SUCCESS';
export const CONNECT_LINK_ERROR = 'app/StripePayoutPage/CONNECT_LINK_ERROR';

export const ONBOARDING_NOTIFIED = 'app/StripePayoutPage/ONBOARDING_NOTIFIED';

// ================ Reducer ================ //

const initialState = {
  balance: null,
  balanceFetchInProgress: false,
  balanceFetchError: null,
  // Connect account state
  connectAccountId: null,
  connectAccountStatus: null,
  createConnectAccountInProgress: false,
  createConnectAccountError: null,
  fetchConnectStatusInProgress: false,
  fetchConnectStatusError: null,
  // A Stripe link to finish or update the account (asked for on a click)
  connectLinkInProgress: false,
  connectLinkError: null,
  // The TradMarket API was told this seller finished onboarding (once per visit)
  onboardingNotified: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_BALANCE_REQUEST:
      return {
        ...state,
        balanceFetchInProgress: true,
        balanceFetchError: null,
      };
    case FETCH_BALANCE_SUCCESS:
      return {
        ...state,
        balance: payload,
        balanceFetchInProgress: false,
      };
    case FETCH_BALANCE_ERROR:
      return {
        ...state,
        balanceFetchInProgress: false,
        balanceFetchError: payload,
      };

    case CREATE_CONNECT_ACCOUNT_REQUEST:
      return {
        ...state,
        createConnectAccountInProgress: true,
        createConnectAccountError: null,
      };
    case CREATE_CONNECT_ACCOUNT_SUCCESS:
      return {
        ...state,
        createConnectAccountInProgress: false,
        connectAccountId: payload.accountId,
      };
    case CREATE_CONNECT_ACCOUNT_ERROR:
      return {
        ...state,
        createConnectAccountInProgress: false,
        createConnectAccountError: payload,
      };

    case FETCH_CONNECT_STATUS_REQUEST:
      return {
        ...state,
        fetchConnectStatusInProgress: true,
        fetchConnectStatusError: null,
      };
    case FETCH_CONNECT_STATUS_SUCCESS:
      return {
        ...state,
        fetchConnectStatusInProgress: false,
        connectAccountId: payload.accountId,
        connectAccountStatus: payload,
      };
    case FETCH_CONNECT_STATUS_ERROR:
      return {
        ...state,
        fetchConnectStatusInProgress: false,
        fetchConnectStatusError: payload,
      };

    case CONNECT_LINK_REQUEST:
      return { ...state, connectLinkInProgress: true, connectLinkError: null };
    case CONNECT_LINK_SUCCESS:
      return { ...state, connectLinkInProgress: false };
    case CONNECT_LINK_ERROR:
      return { ...state, connectLinkInProgress: false, connectLinkError: payload };

    case ONBOARDING_NOTIFIED:
      return { ...state, onboardingNotified: true };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchBalanceRequest = () => ({ type: FETCH_BALANCE_REQUEST });
export const fetchBalanceSuccess = balance => ({
  type: FETCH_BALANCE_SUCCESS,
  payload: balance,
});
export const fetchBalanceError = error => ({
  type: FETCH_BALANCE_ERROR,
  payload: error,
  error: true,
});

export const createConnectAccountRequest = () => ({ type: CREATE_CONNECT_ACCOUNT_REQUEST });
export const createConnectAccountSuccess = data => ({
  type: CREATE_CONNECT_ACCOUNT_SUCCESS,
  payload: data,
});
export const createConnectAccountError = error => ({
  type: CREATE_CONNECT_ACCOUNT_ERROR,
  payload: error,
  error: true,
});

export const fetchConnectStatusRequest = () => ({ type: FETCH_CONNECT_STATUS_REQUEST });
export const fetchConnectStatusSuccess = data => ({
  type: FETCH_CONNECT_STATUS_SUCCESS,
  payload: data,
});
export const fetchConnectStatusError = error => ({
  type: FETCH_CONNECT_STATUS_ERROR,
  payload: error,
  error: true,
});

// ================ Thunks ================ //

/**
 * Create a Stripe Express Connect account and redirect to onboarding
 *
 * Optimized: Creates account AND onboarding link in single request (~2-3s faster)
 */
export const createConnectAccountAndRedirect = (country, businessType, returnUrl, refreshUrl) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(createConnectAccountRequest());

  // Pass URLs to create account + onboarding link in one request (faster!)
  return BackgroundServiceApi.createConnectAccount({ country, businessType, returnUrl, refreshUrl })
    .then(data => {
      if (data.success && data.accountId) {
        dispatch(createConnectAccountSuccess(data));

        // If onboardingUrl returned in same response, redirect immediately
        if (data.onboardingUrl) {
          window.location.href = data.onboardingUrl;
          return;
        }

        // Fallback: Create onboarding link separately (shouldn't happen normally)
        return BackgroundServiceApi.createConnectAccountLink({
          accountId: data.accountId,
          returnUrl,
          refreshUrl,
        }).then(linkData => {
          if (linkData.success && linkData.url) {
            window.location.href = linkData.url;
          } else {
            throw new Error('Failed to create onboarding link');
          }
        });
      } else {
        throw new Error(data.message || 'Failed to create Connect account');
      }
    })
    .catch(e => {
      dispatch(createConnectAccountError(storableError(e)));
      throw e;
    });
};

/**
 * Fetch Connect account status from TradMarket API
 */
export const fetchConnectAccountStatus = accountId => (dispatch, getState, sdk) => {
  dispatch(fetchConnectStatusRequest());

  return BackgroundServiceApi.getConnectAccountStatus(accountId)
    .then(data => {
      if (data.success) {
        dispatch(fetchConnectStatusSuccess(data));
        return data;
      } else {
        throw new Error(data.message || 'Failed to fetch account status');
      }
    })
    .catch(e => {
      dispatch(fetchConnectStatusError(storableError(e)));
      throw e;
    });
};

/**
 * Create an onboarding/update link for existing account, and go to it. One at a time: a
 * click while one is being made does nothing. A failure is kept in connectLinkError.
 * @param {string} type - 'account_onboarding' for new/incomplete accounts, 'account_update' for verified accounts
 */
export const getConnectAccountLink = (
  accountId,
  returnUrl,
  refreshUrl,
  type = 'account_onboarding'
) => (dispatch, getState, sdk) => {
  if (getState().StripePayoutPage.connectLinkInProgress) {
    return Promise.resolve(null);
  }
  dispatch({ type: CONNECT_LINK_REQUEST });
  return BackgroundServiceApi.createConnectAccountLink({
    accountId,
    returnUrl,
    refreshUrl,
    type,
  })
    .then(data => {
      if (!(data.success && data.url)) {
        throw new Error('Failed to create account link');
      }
      dispatch({ type: CONNECT_LINK_SUCCESS });
      window.location.href = data.url;
      return data.url;
    })
    .catch(e => {
      dispatch({ type: CONNECT_LINK_ERROR, payload: storableError(e), error: true });
      return null;
    });
};

/**
 * Opens the seller's Stripe dashboard (a US seller's Express account: their bank details,
 * payouts and tax forms are there). Shares the link's progress and error with
 * getConnectAccountLink: one Stripe link at a time.
 */
export const openStripeDashboard = () => (dispatch, getState) => {
  if (getState().StripePayoutPage.connectLinkInProgress) {
    return Promise.resolve(null);
  }
  dispatch({ type: CONNECT_LINK_REQUEST });
  return BackgroundServiceApi.createConnectLoginLink()
    .then(data => {
      if (!(data.success && data.url)) {
        throw new Error('Failed to create login link');
      }
      dispatch({ type: CONNECT_LINK_SUCCESS });
      window.location.href = data.url;
      return data.url;
    })
    .catch(e => {
      dispatch({ type: CONNECT_LINK_ERROR, payload: storableError(e), error: true });
      return null;
    });
};

// ================ Other Thunks ================ //

/**
 * Where the seller stands, from their orders' line items (the background
 * service's balance, in cents): what pays out next, what waits, what was paid.
 */
export const fetchPayoutBalance = sellerId => dispatch => {
  dispatch(fetchBalanceRequest());
  return BackgroundServiceApi.getSellerBalance(sellerId)
    .then(data => {
      dispatch(fetchBalanceSuccess(data));
      return data;
    })
    .catch(e => {
      dispatch(fetchBalanceError(storableError(e)));
      throw e;
    });
};

/**
 * Notify TradMarket API that seller completed Stripe onboarding
 * This triggers auto-transfer of any available funds
 */
export const notifyOnboardingComplete = (sellerId, accountId) => (dispatch, getState, sdk) => {
  return BackgroundServiceApi.notifyStripeOnboardingComplete({ sellerId, accountId })
    .then(data => {
      if (data.success && data.autoTransfer) {
        // Refresh balance and user data after auto-transfer
        dispatch(fetchPayoutBalance(sellerId));
        dispatch(fetchCurrentUser());
      }
      return data;
    })
    .catch(e => {
      // Log but don't fail - auto-transfer is best-effort
      console.error('Failed to process onboarding completion:', e);
      return { success: false, error: e.message };
    });
};

/**
 * The page's data: the current user, their balance, and their Stripe account's status
 * (when they have one). A failure is kept in the reducer, where the page shows it.
 *
 * Back from Stripe's onboarding (/account/payments/success) with the account able to take
 * payments, the TradMarket API is told, once, so it pays out what was waiting. Only in
 * the browser: the page's server render runs loadData too, and the browser runs it again.
 */
export const loadData = (params = {}) => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUser()).then(() => {
    const currentUser = getState().user.currentUser;
    if (!currentUser?.id) return null;
    const privateData = currentUser.attributes?.profile?.privateData || {};

    // The Connect account's id is in privateData (from the TradMarket API's flow)
    const connectAccountId = privateData.stripeConnectedAccountId;
    const ignore = () => null;

    const sellerId = currentUser.id.uuid;
    const backFromOnboarding = params.returnURLType === 'success' && typeof window !== 'undefined';
    const tellWhenConnected = status => {
      if (!backFromOnboarding || !status?.chargesEnabled) return;
      if (getState().StripePayoutPage.onboardingNotified) return;
      dispatch({ type: ONBOARDING_NOTIFIED });
      return dispatch(notifyOnboardingComplete(sellerId, connectAccountId));
    };

    return Promise.all([
      dispatch(fetchPayoutBalance(sellerId)).catch(ignore),
      connectAccountId
        ? dispatch(fetchConnectAccountStatus(connectAccountId))
            .then(tellWhenConnected)
            .catch(ignore)
        : null,
    ]);
  });
};
